import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { ColumnDef } from '@tanstack/react-table';
import trackingService, { StaleLinkRow } from '../../services/tracking.service';
import { TanTable } from '../../components/TanTable';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';

/**
 * "Not Tracking" admin page (Tracing → Not Tracking).
 *
 * Tracking links with no snapshot update in N hours (default 48; outgated /
 * trace-terminated excluded by the backend). TanTable-powered (search /
 * per-column filter / sort / Excel export); the Pro # opens the order drawer,
 * and each row has a Force Re-Poll action.
 *
 * CSX imports that never got a tracking link (unresolved terminal) live on
 * the separate Tracing → Untracked CSX page.
 */

const formatRelative = (iso: string | null): string => {
  if (!iso) return 'never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60_000);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 48) return `${diffHour}h ago`;
  return `${Math.floor(diffHour / 24)}d ago`;
};

const NotTracking: React.FC = () => {
  const [hours, setHours] = useState<number>(48);
  const [hoursDraft, setHoursDraft] = useState<string>('48');
  const [rows, setRows] = useState<StaleLinkRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [repolling, setRepolling] = useState<Set<number>>(new Set());
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);
  const [drawerPro, setDrawerPro] = useState<number | null>(null);

  const openDrawer = useCallback((pronumber: number) => setDrawerPro(pronumber), []);
  const closeDrawer = useCallback(() => setDrawerPro(null), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await trackingService.listStaleLinks(hours, 500);
      setRows(data.rows);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load stale links');
    } finally {
      setLoading(false);
    }
  }, [hours]);

  useEffect(() => { load(); }, [load]);

  const applyHours = () => {
    const parsed = parseInt(hoursDraft.trim(), 10);
    if (Number.isFinite(parsed) && parsed >= 1 && parsed <= 720) setHours(parsed);
    else setHoursDraft(String(hours));
  };

  const onRepoll = useCallback(async (pronumber: number) => {
    if (!window.confirm(
      `Force re-poll for pronumber ${pronumber}?\n\n`
      + `This re-sends the order's container/BL to gwiTrace's /v1/tracking/import. `
      + `gwiTrace will move the pending poll job to the front of its queue.`,
    )) return;
    setRepolling((prev) => new Set(prev).add(pronumber));
    setToast(null);
    try {
      await trackingService.forceRepoll(pronumber);
      setToast({ kind: 'success', msg: `Re-poll queued at gwiTrace for pro ${pronumber}.` });
      setTimeout(load, 800);
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || 'Re-poll failed';
      setToast({ kind: 'error', msg: `Pro ${pronumber}: ${detail}` });
    } finally {
      setRepolling((prev) => {
        const next = new Set(prev);
        next.delete(pronumber);
        return next;
      });
    }
  }, [load]);

  const proCell = useCallback((v: number | null | undefined) => (
    v != null ? (
      <button
        type="button"
        onClick={() => openDrawer(Number(v))}
        className="text-blue-600 dark:text-blue-400 hover:underline"
        title="Open order"
      >
        {v}
      </button>
    ) : <span className="text-gray-400">—</span>
  ), [openDrawer]);

  const dash = (v: string | null) => v || <span className="text-gray-400">—</span>;

  const columns = useMemo<ColumnDef<StaleLinkRow>[]>(() => [
    { accessorKey: 'pronumber', header: 'Pro #', cell: (i) => proCell(i.getValue() as number), size: 100 },
    { accessorKey: 'container', header: 'Container', cell: (i) => dash(i.getValue() as string | null), size: 140 },
    { accessorKey: 'bol', header: 'BOL', cell: (i) => dash(i.getValue() as string | null), size: 170 },
    { accessorKey: 'scac', header: 'SCAC', cell: (i) => dash(i.getValue() as string | null), size: 90 },
    { accessorKey: 'rail', header: 'Rail', cell: (i) => dash(i.getValue() as string | null), size: 90 },
    {
      id: 'last_synced_at', header: 'Last synced',
      accessorFn: (r) => r.last_synced_at ?? '',
      cell: (i) => {
        const v = i.row.original.last_synced_at;
        return <span className={v ? '' : 'text-orange-600 dark:text-orange-400 font-medium'}>{formatRelative(v)}</span>;
      },
      size: 120,
    },
    {
      id: 'gwitrace_last_carrier_data_at', header: 'Carrier data',
      accessorFn: (r) => r.gwitrace_last_carrier_data_at ?? '',
      cell: (i) => formatRelative(i.row.original.gwitrace_last_carrier_data_at),
      size: 120,
    },
    {
      accessorKey: 'last_error', header: 'Last error',
      cell: (i) => {
        const v = i.getValue() as string | null;
        return v ? <span className="text-xs text-red-600 dark:text-red-400" title={v}>{v.slice(0, 80)}</span> : <span className="text-gray-400">—</span>;
      },
      size: 220,
    },
    {
      id: 'action', header: '', enableSorting: false, enableColumnFilter: false,
      cell: (i) => {
        const pro = i.row.original.pronumber;
        return (
          <button
            onClick={() => onRepoll(pro)}
            disabled={repolling.has(pro)}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900/50 disabled:opacity-50 rounded transition-colors"
          >
            <ArrowPathIcon className={`h-3 w-3 ${repolling.has(pro) ? 'animate-spin' : ''}`} />
            Force Re-Poll
          </button>
        );
      },
      size: 130,
    },
  ], [proCell, onRepoll, repolling]);

  return (
    <div className="p-4 pb-24">
      <div className="mb-4">
        <h1 className="text-xl font-bold text-heading">Not Tracking</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Tracking links with no snapshot update in the last {hours} hours. Outgated and
          trace-terminated orders are excluded. Click a Pro # to open the order.
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-2 mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
          <div className="text-sm text-red-800 dark:text-red-200">{error}</div>
        </div>
      )}

      {toast && (
        <div className={`mb-3 p-3 rounded text-sm ${toast.kind === 'success'
          ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200'
          : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'}`}>
          {toast.msg}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-2 text-xs">
        <label className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
          No update in
          <input
            type="number" min={1} max={720} value={hoursDraft}
            onChange={(e) => setHoursDraft(e.target.value)}
            onBlur={applyHours}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyHours(); } }}
            className="w-16 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-panel text-heading"
          />
          hours
        </label>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-medium rounded-md transition-colors"
        >
          <ArrowPathIcon className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <TanTable
        data={rows}
        columns={columns}
        loading={loading}
        settingsKey="not-tracking-stale-links"
        cardTitle="Not Tracking"
        searchPlaceholder="Search stale links…"
        enableSearch={true}
        enableColumnFilters={true}
        enableShowHideCols={true}
        enableReset={true}
        enableExcelExport={true}
        enableZebraStriping={true}
        showGridLines={true}
        disableVirtualization={true}
        columnFilterTypes={{
          pronumber: 'text', container: 'text', bol: 'text',
          last_synced_at: 'text', gwitrace_last_carrier_data_at: 'text', last_error: 'text',
        }}
        defaultSorting={[{ id: 'last_synced_at', desc: false }]}
      />

      <ProDetailSlideOver
        pronumber={drawerPro}
        isOpen={drawerPro !== null}
        onClose={closeDrawer}
      />
    </div>
  );
};

export default NotTracking;
